/* =============================================================
   Header.jsx — Admin portal top navigation bar
   -------------------------------------------------------------
   Fixed header that sits above all admin screens.
   Contains: global search, notification bell, help button,
   and the logged-in user's avatar with initials.

   The left edge of the header shifts to match the sidebar width
   (280px expanded, 72px collapsed) via inline style.
   ============================================================= */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';
import { useAuth }    from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import GlobalSearch     from './GlobalSearch';


export default function Header() {
  const { open }       = useSidebar();
  const { user, logout } = useAuth();
  const navigate       = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef   = useRef(null);
  const leaveTimer = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(leaveTimer.current);
    setMenuOpen(true);
  };
  const handleMouseLeave = () => {
    leaveTimer.current = setTimeout(() => setMenuOpen(false), 150);
  };

  return (
    <div
      className="fixed top-0 right-0 h-16 bg-white/80 backdrop-blur-sm border-b border-slate-100 flex items-center px-8 gap-4 z-30 transition-all duration-300"
      style={{ left: open ? '280px' : '72px' }}
    >
      {/* Global search */}
      <GlobalSearch />

      <div className="ml-auto flex items-center gap-2">
        {/* Notification bell */}
        <NotificationBell />

        {/* User avatar + dropdown */}
        <div
          className="relative ml-1"
          ref={menuRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold hover:opacity-90 transition-opacity focus:outline-none"
            style={{ backgroundColor: user?.avatarBg ?? '#4343d5' }}
          >
            {user?.initials ?? '??'}
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-11 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1.5 z-50">
              {/* User info */}
              <div className="px-4 py-2.5 border-b border-slate-100">
                <p className="text-xs font-semibold text-[#0b1c30] truncate">{user?.name ?? '—'}</p>
                <p className="text-[11px] text-slate-400 truncate mt-0.5">{user?.email ?? ''}</p>
              </div>

              {/* Settings */}
              <button
                onClick={() => navigate('/settings')}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#464555] hover:bg-slate-50 transition-colors"
              >
                <span className="material-symbols-outlined text-[17px] text-slate-400">settings</span>
                Settings
              </button>

              {/* Sign out */}
              <button
                onClick={() => logout()}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
              >
                <span className="material-symbols-outlined text-[17px]">logout</span>
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
